import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { getDynamicOfSalesChartAreaApi } from "@src/api";
import type {
    DynamicOfSalesChartAreaQueryParams,
    DynamicOfSalesChartAreaResponse,
    DynamicOfSalesGranularity,
    ErrorResponseType,
} from "@src/types";

export interface UseDynamicOfSalesChartAreaParams {
    merchantProductId?: string;
    cityId?: string | null;
    dateFrom?: string | null;
    dateTo?: string | null;
    granularity?: DynamicOfSalesGranularity | null;
    enabled?: boolean;
}

export function useDynamicOfSalesChartArea({
    merchantProductId,
    cityId,
    dateFrom,
    dateTo,
    granularity,
    enabled = true,
}: UseDynamicOfSalesChartAreaParams) {
    const params = useMemo<DynamicOfSalesChartAreaQueryParams | null>(() => {
        if (!merchantProductId) return null;

        return {
            merchant_product_id: merchantProductId,
            city_id: cityId?.trim() ? cityId.trim() : undefined,
            date_from: dateFrom ?? undefined,
            date_to: dateTo ?? undefined,
            granularity: granularity ?? undefined,
        };
    }, [cityId, dateFrom, dateTo, granularity, merchantProductId]);

    const canFetch = enabled && Boolean(params?.merchant_product_id);

    return useQuery<DynamicOfSalesChartAreaResponse, AxiosError<ErrorResponseType>>({
        queryKey: ["reporting", "dynamic-of-sales", "chart-area", params],
        queryFn: ({ signal }) =>
            getDynamicOfSalesChartAreaApi(params as DynamicOfSalesChartAreaQueryParams, signal),
        enabled: canFetch,
        refetchOnWindowFocus: false,
    });
}
